import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { EllipsisText, formatDateTime, JsonBlock } from '../Assessments/shared';
import { cancelJob, fetchJob, retryJob } from './api';
import { readableError } from './errors';
import {
  canCancelJob,
  canRetryJob,
  describeActor,
  formatDuration,
  isJobLive,
  JobStatusTag,
  jobDurationSeconds,
  jobKindLabel,
  jobOutcome,
  TARGET_KIND_LABELS,
  useNow,
  usePolling,
} from './Jobs';
import { JobLink, JsonObject, ScenarioJob } from './types';

/**
 * How late a RUNNING job's heartbeat is, or null when it is not late.
 *
 * A live job renews its lease every `heartbeat_seconds`; a lease is the window
 * in which a KILLED worker's row is still believed, and an encoding
 * `score.preview` is leased an hour so a fifty-minute run is not declared dead
 * for being slow. The cost of that hour is a job that stays RUNNING long after
 * its worker died — holding the one-live-job-per-target lock, refusing every
 * new run against that target with "wait for it to finish". The row already
 * knows, because its last beat stopped moving. Three missed beats is the
 * threshold: one missed beat is a slow query or a poll landing between writes.
 */
export const silentFor = (
  job: ScenarioJob,
  now: number,
): { seconds: number; interval: number } | null => {
  if (job.status !== 'RUNNING' || !job.lease_heartbeat_at) return null;
  const interval = Number(job.heartbeat_seconds || 0);
  if (!interval || interval <= 0) return null;
  const seconds = (now - Date.parse(job.lease_heartbeat_at)) / 1000;
  if (!Number.isFinite(seconds) || seconds < interval * 3) return null;
  return { seconds, interval };
};

/**
 * One job, in full: what was asked, what came back, what it wrote, and the
 * tail of what it said while it ran. This is the page an audit lands on, so
 * nothing is summarized away — the artifacts are folded, never dropped.
 */

/**
 * The payload keys that hold a whole rendered artifact rather than an
 * option. They are what makes "confirm exactly what you previewed" true, so
 * they are shown behind a fold with their size stated, not truncated and
 * not hidden.
 */
export const JOB_ARTIFACT_KEYS: Record<string, string> = {
  telling:
    'The rendered telling this job stored. An apply mints it verbatim and never re-renders, which is what makes a confirmation mean what it says.',
  benchmark_payload:
    'The assembled answer key this job stored. The push ingests it verbatim.',
};

export type JobArtifact = {
  key: string;
  note: string;
  characters: number;
  value: unknown;
};

export const splitJobArtifacts = (
  payload: JsonObject | undefined,
): { options: JsonObject; artifacts: JobArtifact[] } => {
  const options: JsonObject = {};
  const artifacts: JobArtifact[] = [];
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (key in JOB_ARTIFACT_KEYS && value != null && value !== '') {
      artifacts.push({
        key,
        note: JOB_ARTIFACT_KEYS[key],
        characters: JSON.stringify(value).length,
        value,
      });
      return;
    }
    options[key] = value;
  });
  return { options, artifacts };
};

/** Where a produced thing lives. The backend may send the route itself; a
 *  link without one is resolved from the kinds this admin already routes. */
export const jobLinkHref = (link: JobLink): string | null => {
  if (link.url) return link.url;
  if (link.id == null) return null;
  if (link.kind === 'scenario_draft') {
    return `/dashboard/scenario-builder?draft=${link.id}`;
  }
  if (link.kind === 'blueprint' || link.kind === 'blueprint_version') {
    return `/dashboard/blueprints/${link.id}`;
  }
  if (link.kind === 'assessment_scenario') {
    return `/dashboard/assessments?scenario=${encodeURIComponent(String(link.id))}`;
  }
  return null;
};

const ChainLink: React.FC<{
  label: string;
  id: string | null | undefined;
  onOpen: (id: string) => void;
}> = ({ label, id, onOpen }) => {
  if (!id) return null;
  return (
    <Space size={6}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Link onClick={() => onOpen(id)}>
        {String(id).slice(0, 8)}
      </Typography.Link>
    </Space>
  );
};

type Props = {
  jobId: string;
  onBack: () => void;
  onOpen?: (id: string) => void;
  onOpenTarget?: (kind: string, id: number) => void;
};

const JobDetail: React.FC<Props> = ({
  jobId,
  onBack,
  onOpen,
  onOpenTarget,
}) => {
  const [job, setJob] = useState<ScenarioJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      try {
        setJob(await fetchJob(jobId));
        setError('');
      } catch (requestError) {
        setError(readableError(requestError, { what: 'That job' }));
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [jobId],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  // A terminal job cannot change again, so polling stops entirely rather
  // than backing off: an audit page that keeps asking is an audit page that
  // keeps spending someone's session on an answer that is already final.
  const live = isJobLive(job?.status);
  usePolling(() => void load(), { live, enabled: live });

  const now = useNow(1000, job?.status === 'RUNNING');
  const silence = job ? silentFor(job, now) : null;

  if (loading && !job) {
    return (
      <div className="assessment-loading">
        <Spin size="large" tip="Loading job" />
      </div>
    );
  }
  if (!job) {
    return (
      <Result
        status="warning"
        title="This job could not be loaded"
        subTitle={error || 'The job ledger returned no row for this id.'}
        extra={
          <Button onClick={onBack} icon={<ArrowLeftOutlined />}>
            Back to the job ledger
          </Button>
        }
      />
    );
  }

  const outcome = jobOutcome(job);
  const { options, artifacts } = splitJobArtifacts(job.request_payload);
  const links = job.links || [];
  const log = job.log || [];

  const runCommand = async (command: (id: string) => Promise<ScenarioJob>) => {
    setBusy(true);
    setActionError('');
    try {
      const next = await command(job.id);
      // A retry answers with the NEW row; following it keeps the admin on
      // the attempt they just started instead of the one they left behind.
      if (next && next.id && next.id !== job.id && onOpen) onOpen(next.id);
      else await load();
    } catch (commandError) {
      setActionError(readableError(commandError, { what: 'That job' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="assessment-admin">
      <div className="assessment-workspace-header">
        <Space size={16} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back to the job ledger
          </Button>
          <Typography.Title className="assessment-title" level={3}>
            {jobKindLabel(job.kind)}
          </Typography.Title>
          <JobStatusTag status={job.status} />
          <Typography.Text code copyable>
            {job.id}
          </Typography.Text>
        </Space>
        <Space>
          {canCancelJob(job) && (
            <Button loading={busy} onClick={() => runCommand(cancelJob)}>
              Cancel
            </Button>
          )}
          {canRetryJob(job) && (
            <Button loading={busy} onClick={() => runCommand(retryJob)}>
              Run again
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => load(true)}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      {actionError && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={actionError}
        />
      )}

      <Alert
        showIcon
        type={
          outcome.tone === 'success'
            ? 'success'
            : outcome.tone === 'warning'
              ? 'warning'
              : outcome.tone === 'error'
                ? 'error'
                : 'info'
        }
        title={outcome.title}
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {outcome.gate && (
              <Typography.Text type="secondary">
                Raised by {outcome.gate}.
              </Typography.Text>
            )}
            {outcome.detail && <span>{outcome.detail}</span>}
            {outcome.remedy && (
              <span>
                <strong>Remedy: </strong>
                {outcome.remedy}
              </span>
            )}
            {outcome.confirmable && (
              <span>
                This check can be answered. Open the target and re-submit the
                action there, where the differences it is warning about are
                shown beside the confirmation.
              </span>
            )}
          </Space>
        }
      />

      {silence && (
        <Alert
          className="assessment-callout"
          data-testid="job-silent-worker"
          type="warning"
          showIcon
          title={`This job has not reported in for ${formatDuration(
            silence.seconds,
          )}`}
          description={
            <Space direction="vertical" size={4}>
              <span>
                {`A running job renews its lease every ${formatDuration(
                  silence.interval,
                )}, so its worker has most likely stopped. Nothing can end a run mid-transaction: the lease is what records it.`}
              </span>
              <span>
                {job.lease_expires_at
                  ? `If it stays silent it is recorded as lost at ${formatDateTime(
                      job.lease_expires_at,
                    )}, and what survived is stated on the row then. Until it is, this job holds its target and a new run against it is refused.`
                  : 'It holds its target until the sweep records it, and a new run against that target is refused until then.'}
              </span>
            </Space>
          }
        />
      )}

      <Card title="What was asked" size="small">
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Kind">
            <EllipsisText code value={job.kind} />
          </Descriptions.Item>
          <Descriptions.Item label="Lane">
            <Tag color={job.lane === 'heavy' ? 'purple' : 'default'}>
              {String(job.lane || 'slim').toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Raised">
            <Tag>{String(job.trigger || 'manual').toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Target">
            {job.target_id && onOpenTarget ? (
              <Typography.Link
                onClick={() => onOpenTarget(job.target_kind, job.target_id)}
              >
                {job.target_label ||
                  `${TARGET_KIND_LABELS[job.target_kind] || job.target_kind} #${job.target_id}`}
              </Typography.Link>
            ) : (
              <EllipsisText
                value={
                  job.target_label ||
                  TARGET_KIND_LABELS[job.target_kind] ||
                  job.target_kind
                }
              />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Requested by">
            {describeActor(job.created_by)}
          </Descriptions.Item>
          <Descriptions.Item label="Written as">
            {describeActor(job.acts_as || job.created_by)}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {formatDateTime(job.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Started">
            {formatDateTime(job.started_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Finished">
            {formatDateTime(job.finished_at)}
          </Descriptions.Item>
          <Descriptions.Item label="Ran for">
            {formatDuration(jobDurationSeconds(job, now))}
          </Descriptions.Item>
          <Descriptions.Item label="Attempt">
            {job.attempt || 1}
          </Descriptions.Item>
          <Descriptions.Item label="Worker lease">
            {job.lease_owner ? (
              <Space direction="vertical" size={0}>
                <EllipsisText code value={job.lease_owner} />
                <Typography.Text type="secondary">
                  last heartbeat {formatDateTime(job.lease_heartbeat_at)}
                </Typography.Text>
              </Space>
            ) : (
              <Typography.Text type="secondary">
                No worker has claimed this job.
              </Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
        <Space className="assessment-help-copy" size={16} wrap>
          <ChainLink
            label="Confirmed the preview"
            id={job.preview_job}
            onOpen={id => onOpen?.(id)}
          />
          <ChainLink
            label="Chained from"
            id={job.parent_job}
            onOpen={id => onOpen?.(id)}
          />
          <ChainLink
            label="Superseded by"
            id={job.superseded_by}
            onOpen={id => onOpen?.(id)}
          />
        </Space>
      </Card>

      <Card title="What it created" size="small">
        {links.length ? (
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {links.map((link, index) => {
              const href = jobLinkHref(link);
              const label = link.label || `${link.kind || link.rel} ${link.id}`;
              return (
                <Space key={`${link.rel}-${index}`} size={8}>
                  <Tag>{link.rel}</Tag>
                  {href ? <Link to={href}>{label}</Link> : <span>{label}</span>}
                </Space>
              );
            })}
          </Space>
        ) : (
          <Typography.Text type="secondary">
            This job wrote nothing that can be linked to. A refused or failed
            job leaves no record beyond this page, which is why this page
            exists.
          </Typography.Text>
        )}
      </Card>

      <Card title="Result" size="small">
        <JsonBlock value={job.result || {}} />
      </Card>

      <Card title="Request" size="small">
        <JsonBlock value={options} />
        {artifacts.map(artifact => (
          <Collapse
            key={artifact.key}
            className="assessment-inner-card"
            items={[
              {
                key: artifact.key,
                label: `${artifact.key} — ${artifact.characters.toLocaleString()} characters`,
                children: (
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: '100%' }}
                  >
                    <Typography.Text type="secondary">
                      {artifact.note}
                    </Typography.Text>
                    <JsonBlock value={artifact.value} />
                  </Space>
                ),
              },
            ]}
          />
        ))}
      </Card>

      {(job.refusal?.details || job.error?.traceback_tail) && (
        <Card title="Evidence" size="small">
          {job.refusal?.details && <JsonBlock value={job.refusal.details} />}
          {job.error?.traceback_tail && (
            <pre className="assessment-json">{job.error.traceback_tail}</pre>
          )}
        </Card>
      )}

      <Card title={`Log (${log.length} lines)`} size="small">
        {log.length ? (
          <pre className="assessment-json">{log.join('\n')}</pre>
        ) : (
          <Typography.Text type="secondary">
            This job recorded no log lines.
          </Typography.Text>
        )}
      </Card>
    </div>
  );
};

export default JobDetail;
